import { datadogLogs } from '@datadog/browser-logs'

import { API_URL, clearSessionToken, getSessionToken, setSessionToken } from '../config/config'
import { mockWidgetSettings } from '../config/mockSettings'

export const SESSION_HEADER = 'X-Neva-Session'
const REQUESTED_WITH_HEADER = 'X-Requested-With'
const REQUESTED_WITH_VALUE = 'neva-widget'

const noop = () => undefined

let onUnauthenticated = noop

/*
 * Called when the server answers 401: the session credential is missing, expired or covers another chat. The
 * server never takes a chatId from the request any more, so there is nothing to retry with; the owner of the
 * conversation has to start a new one.
 */
export const setUnauthenticatedHandler = handler => {
  onUnauthenticated = typeof handler === 'function' ? handler : noop
}

export const createJsonHeaders = () => {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return headers
}

export const captureSessionToken = response => {
  const token = response?.headers?.get?.(SESSION_HEADER)
  if (token) {
    setSessionToken(token)
  }
  return response
}

/*
 * Every call to the widget API goes through here: it carries the session credential both ways (the cookie via
 * credentials: 'include', the header fallback explicitly) and the X-Requested-With header the server requires on
 * state-changing calls.
 */
export const apiFetch = (path, { headers, json = true, ...options } = {}) => {
  const requestHeaders = json ? createJsonHeaders() : new Headers()
  new Headers(headers).forEach((value, name) => requestHeaders.set(name, value))
  requestHeaders.set(REQUESTED_WITH_HEADER, REQUESTED_WITH_VALUE)

  const token = getSessionToken()
  if (token) {
    requestHeaders.set(SESSION_HEADER, token)
  }

  return fetch(`${API_URL}${path}`, {
    ...options,
    headers: requestHeaders,
    credentials: 'include'
  }).then(response => {
    captureSessionToken(response)
    if (response.status === 401) {
      clearSessionToken()
      onUnauthenticated()
    }
    return response
  })
}

export function logoutOnRequestOrResponseJson(response) {
  if (response.ok) {
    return response.json()
  } else {
    throw Error(response.status + ' - ' + response.statusText)
  }
}

export const getWidgetSettings = botId => {
  return apiFetch(`/natera/widget/info/${botId}`, {
    method: 'GET'
  })
    .then(response => {
      if (response.ok) {
        return response.json().then(settings => settings.data)
      }
      if (response.status >= 400) {
        response.text().then(err => datadogLogs.logger.error((JSON.parse(err).message)))
        return mockWidgetSettings
      }
    })
    .catch(error => datadogLogs.logger.error(error))
}

export const getPersistentMenuApi = async botId => {
  return apiFetch(`/widget/${botId}/persistentMenu`, {
    method: 'GET'
  })
    .then(response => {
      if (response.ok) {
        return response.json().then(settings => settings)
      }
    })
    .catch(error => datadogLogs.logger.error(error))
}

export const getContentForWebviewModal = webviewId =>
  apiFetch(`/webview/${webviewId}`, {
    method: 'GET'
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .catch(error => datadogLogs.logger.error(error))

/*
 * chatId is only a guard here — an action needs a conversation to belong to — and never goes on the wire: the
 * server takes the chat from the session.
 */
export const sendAction = (actionType, botId, chatId, metadata) => {
  const event = {
    action: {
      type: actionType,
      metadata: metadata
    }
  }

  if (chatId) {
    return apiFetch(`/widget/${botId}/chat/action`, {
      method: 'POST',
      body: JSON.stringify(event)
    }).catch(error => datadogLogs.logger.error(error))
  }
}

export const getGreetingText = (botId, attributes, language) => {
  let url = `/widget/${botId}/greeting`

  attributes.forEach(attribute => {
    url = url.addQueryStringParameter(attribute.name, attribute.value)
  })

  if (language) {
    url = url.addQueryStringParameter('language', language)
  }

  return apiFetch(url, {
    method: 'GET'
  })
    .then(response => {
      if (response.ok) {
        return response.json().then(greetingText => greetingText.data)
      }
    })
    .catch(error => datadogLogs.logger.error(error))
}

export const sendOpenedGreeting = (botId, hash, chatId) => {
  if (chatId) {
    apiFetch(`/widget/${botId}/greeting/opened/${hash}`, {
      method: 'POST'
    }).catch(error => datadogLogs.logger.error(error))
  }
}

export const sendSentGreeting = (botId, hash) => {
  apiFetch(`/widget/${botId}/greeting/sent/${hash}`, {
    method: 'POST'
  }).catch(error => datadogLogs.logger.error(error))
}
